/**
 * useFhirPatient Hook
 *
 * Repository-pattern hook for fetching FHIR Patient data from the
 * dual-write storage (DRR-080). Parses fhirJson from FhirStoredResourceType.
 *
 * FERP Principles:
 * - Repository pattern: GraphQL query → parsed FHIR R4 Patient
 * - Dumb frontend: Just fetches and parses, no clinical logic
 * - DRR-123: Clinical data — requires clinical:read scope on backend
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL query — reads from FHIR dual-write storage (DRR-080)
// ============================================================================

const ADMIN_LIST_FHIR_PATIENTS = gql`
  query AdminListFhirPatientsHook($tenantNum: Int!, $limit: Int = 50) {
    adminListFhirPatients(tenantNum: $tenantNum, limit: $limit) {
      resourceId
      resourceType
      fhirJson
      createdAt
      isLatest
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface FhirPatient {
  id: string;
  resourceId: string;
  name?: string;
  mrn?: string;
  dateOfBirth?: string;
  gender?: string;
  status?: string;
  address?: string;
  phone?: string;
  email?: string;
  [key: string]: unknown;
}

export interface UseFhirPatientResult {
  patient: FhirPatient | null;
  patients: FhirPatient[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============================================================================
// FHIR R4 JSON parsing helpers
// ============================================================================

function extractName(fhir: Record<string, unknown>): string | undefined {
  const nameArr = fhir.name as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(nameArr) || nameArr.length === 0) return undefined;
  const entry = nameArr[0];
  const given = Array.isArray(entry.given) ? (entry.given as string[]).join(' ') : '';
  const family = typeof entry.family === 'string' ? entry.family : '';
  return [given, family].filter(Boolean).join(' ') || undefined;
}

function extractTelecom(fhir: Record<string, unknown>, system: string): string | undefined {
  const telecom = fhir.telecom as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(telecom)) return undefined;
  const entry = telecom.find((t) => t.system === system);
  return typeof entry?.value === 'string' ? entry.value : undefined;
}

function extractAddress(fhir: Record<string, unknown>): string | undefined {
  const addrArr = fhir.address as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(addrArr) || addrArr.length === 0) return undefined;
  const addr = addrArr[0];
  const lines = Array.isArray(addr.line) ? (addr.line as string[]).join(', ') : '';
  const parts = [lines, addr.city, addr.state, addr.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

function parseFhirPatient(resourceId: string, fhirJson: string): FhirPatient | null {
  try {
    const fhir = JSON.parse(fhirJson) as Record<string, unknown>;
    return {
      id: (fhir.id as string) ?? resourceId,
      resourceId,
      name: extractName(fhir),
      mrn: typeof fhir.identifier === 'string' ? fhir.identifier : undefined,
      dateOfBirth: typeof fhir.birthDate === 'string' ? fhir.birthDate : undefined,
      gender: typeof fhir.gender === 'string' ? fhir.gender : undefined,
      status: fhir.active === true ? 'active' : fhir.active === false ? 'inactive' : undefined,
      address: extractAddress(fhir),
      phone: extractTelecom(fhir, 'phone'),
      email: extractTelecom(fhir, 'email'),
    };
  } catch {
    return null;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetch FHIR Patients for a tenant.
 *
 * @param patientId - Optional: filter to a specific patient resourceId
 * @param tenantNum - Numeric tenant ID from auth context (required, no hardcoded default)
 */
export function useFhirPatient(
  patientId: string | null | undefined,
  tenantNum: number,
): UseFhirPatientResult {
  const { data, loading, error, refetch } = useQuery(ADMIN_LIST_FHIR_PATIENTS, {
    variables: { tenantNum, limit: 50 },
    skip: !tenantNum,
    fetchPolicy: 'cache-first',
  });

  const patients = useMemo<FhirPatient[]>(() => {
    const items = data?.adminListFhirPatients ?? [];
    return items
      .filter((r: { isLatest: boolean }) => r.isLatest)
      .map((r: { resourceId: string; fhirJson: string }) => parseFhirPatient(r.resourceId, r.fhirJson))
      .filter(Boolean) as FhirPatient[];
  }, [data]);

  const patient = useMemo<FhirPatient | null>(() => {
    if (!patientId) return patients[0] ?? null;
    return patients.find((p) => p.resourceId === patientId || p.id === patientId) ?? null;
  }, [patients, patientId]);

  return {
    patient,
    patients,
    loading,
    error: error ? new Error(error.message) : null,
    refetch,
  };
}
